import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Flavor,
  Mode,
  ModePreference,
  Density,
  COLOR_TOKENS,
  DENSITY_TOKENS,
  ColorTokens,
  DensityTokens,
} from '../theme/signatureTokens';
import { buildSignatureMuiTheme } from '../theme/signatureTheme';
import { appearanceApi, UiPreferences } from '../api/appearance';
import { useAuth } from './AuthContext';

interface SignaturePrefs {
  flavor: Flavor;
  modePref: ModePreference;
  density: Density;
  locale: string;
}

interface SignatureContextType {
  flavor: Flavor;
  /** Modo efetivo já resolvido (preferência 'auto' vira 'light'/'dark' conforme o sistema). */
  mode: Mode;
  /** Preferência escolhida pelo usuário, que pode ser 'auto'. */
  modePref: ModePreference;
  density: Density;
  locale: string;
  colors: ColorTokens;
  densities: DensityTokens;
  setFlavor: (f: Flavor) => void;
  setModePreference: (m: ModePreference) => void;
  toggleMode: () => void;
  setDensity: (d: Density) => void;
  setLocale: (l: string) => void;
  /** Persiste as preferências (local + backend por usuário+empresa) e propaga erros do servidor. */
  savePreferences: (partial: Partial<SignaturePrefs>) => Promise<void>;
}

const FLAVORS: Flavor[] = ['SIGNATURE', 'EXECUTIVE', 'PROCESS', 'MINIMAL'];
const DENSITIES: Density[] = ['comfortable', 'compact'];
const MODE_PREFS: ModePreference[] = ['light', 'dark', 'auto'];

const DEFAULT_STATE: SignaturePrefs = {
  flavor: 'SIGNATURE',
  modePref: 'light',
  density: 'comfortable',
  locale: 'pt-BR',
};

const SignatureContext = createContext<SignatureContextType | undefined>(undefined);

/** Cache local das preferências. Aceita o formato anterior ({ mode }) para não perder escolhas existentes. */
function readLocalPrefs(storageKey: string): SignaturePrefs {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const raw = JSON.parse(saved) || {};
      const legacyMode: ModePreference | undefined =
        raw.mode === 'dark' ? 'dark' : raw.mode === 'light' ? 'light' : undefined;
      return {
        flavor: FLAVORS.includes(raw.flavor) ? raw.flavor : DEFAULT_STATE.flavor,
        modePref: MODE_PREFS.includes(raw.modePref) ? raw.modePref : (legacyMode ?? DEFAULT_STATE.modePref),
        density: DENSITIES.includes(raw.density) ? raw.density : DEFAULT_STATE.density,
        locale: typeof raw.locale === 'string' && raw.locale ? raw.locale : DEFAULT_STATE.locale,
      };
    }
  } catch {
    // fallback
  }
  return DEFAULT_STATE;
}

/**
 * Normaliza a resposta do backend. Ignora o formato legado ({ themeId, mode }) para não
 * sobrescrever a preferência local de quem ainda não salvou pelo contrato novo.
 */
function fromRemote(ui: Record<string, unknown> | null | undefined): Partial<SignaturePrefs> | null {
  if (!ui) return null;
  if (typeof ui.flavor !== 'string' && typeof ui.modePref !== 'string') return null;

  const out: Partial<SignaturePrefs> = {};
  if (FLAVORS.includes(ui.flavor as Flavor)) out.flavor = ui.flavor as Flavor;
  if (MODE_PREFS.includes(ui.modePref as ModePreference)) out.modePref = ui.modePref as ModePreference;
  if (DENSITIES.includes(ui.density as Density)) out.density = ui.density as Density;
  if (typeof ui.locale === 'string' && ui.locale) out.locale = ui.locale;
  return out;
}

function hasSessionToken(): boolean {
  try {
    return !!sessionStorage.getItem('access_token');
  } catch {
    return false;
  }
}

const SignatureMuiBridge: React.FC<{ children: React.ReactNode; colors: ColorTokens; densities: DensityTokens; mode: Mode }> = ({
  children,
  colors,
  densities,
  mode,
}) => {
  const muiTheme = useMemo(() => buildSignatureMuiTheme(colors, densities), [colors, densities]);

  // Sincroniza a classe .dark no HTML/Body para Tailwind
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (mode === 'dark') {
      root.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      root.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [mode]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

export const SignatureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getEmpresaId } = useAuth();
  const empresaId = getEmpresaId();

  const storageKey = useMemo(() => {
    const tenantId = empresaId ? String(empresaId) : 'default';
    return `galatea_signature_prefs_${tenantId}`;
  }, [empresaId]);

  const [prefs, setPrefs] = useState<SignaturePrefs>(() => readLocalPrefs(storageKey));
  const prefsRef = useRef<SignaturePrefs>(prefs);

  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);

  // Atualiza o cache local ao trocar de empresa (tenant)
  useEffect(() => {
    const local = readLocalPrefs(storageKey);
    prefsRef.current = local;
    setPrefs(local);
  }, [storageKey]);

  // Preferência de luminosidade do sistema operacional (usada quando modePref === 'auto')
  const [systemDark, setSystemDark] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Hidrata as preferências a partir do backend (fonte da verdade por usuário + empresa)
  useEffect(() => {
    if (!empresaId || !hasSessionToken()) return;
    let cancelled = false;

    (async () => {
      try {
        const { ui } = await appearanceApi.get(empresaId);
        if (cancelled) return;
        const remote = fromRemote(ui);
        if (!remote) return; // sem preferências nossas no servidor: mantém o cache local
        const merged = { ...readLocalPrefs(storageKey), ...remote };
        prefsRef.current = merged;
        setPrefs(merged);
        try {
          localStorage.setItem(storageKey, JSON.stringify(merged));
        } catch {
          // ignore
        }
      } catch {
        // Backend indisponível: mantém o cache local
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [empresaId, storageKey]);

  const syncRemote = useCallback(
    (next: SignaturePrefs): Promise<void> => {
      if (!empresaId || !hasSessionToken()) return Promise.resolve();
      const ui: UiPreferences = {
        flavor: next.flavor,
        modePref: next.modePref,
        density: next.density,
        locale: next.locale,
      };
      return appearanceApi.savePreferences(empresaId, ui).then(() => undefined);
    },
    [empresaId]
  );

  /** Aplica a preferência no runtime (estado + cache local) e devolve o objeto atualizado. */
  const applyPrefs = (partial: Partial<SignaturePrefs>): SignaturePrefs => {
    const updated = { ...prefsRef.current, ...partial };
    prefsRef.current = updated;
    setPrefs(updated);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch {
      // ignore
    }
    return updated;
  };

  const fireAndForget = (promise: Promise<void>) => {
    promise.catch(() => {
      // O cache local permanece válido se o backend recusar a gravação
    });
  };

  const setFlavor = (flavor: Flavor) => fireAndForget(syncRemote(applyPrefs({ flavor })));
  const setDensity = (density: Density) => fireAndForget(syncRemote(applyPrefs({ density })));
  const setLocale = (locale: string) => fireAndForget(syncRemote(applyPrefs({ locale })));
  const setModePreference = (modePref: ModePreference) => fireAndForget(syncRemote(applyPrefs({ modePref })));

  const mode: Mode = prefs.modePref === 'auto' ? (systemDark ? 'dark' : 'light') : prefs.modePref;

  const toggleMode = () => setModePreference(mode === 'dark' ? 'light' : 'dark');

  const savePreferences = (partial: Partial<SignaturePrefs>): Promise<void> => {
    return syncRemote(applyPrefs(partial));
  };

  const colors = useMemo(() => COLOR_TOKENS[prefs.flavor][mode], [prefs.flavor, mode]);
  const densities = useMemo(() => DENSITY_TOKENS[prefs.density], [prefs.density]);

  const value = useMemo(
    () => ({
      flavor: prefs.flavor,
      mode,
      modePref: prefs.modePref,
      density: prefs.density,
      locale: prefs.locale,
      colors,
      densities,
      setFlavor,
      setModePreference,
      toggleMode,
      setDensity,
      setLocale,
      savePreferences,
    }),
    [prefs.flavor, prefs.modePref, prefs.density, prefs.locale, mode, colors, densities]
  );

  return (
    <SignatureContext.Provider value={value}>
      <SignatureMuiBridge colors={colors} densities={densities} mode={mode}>
        {children}
      </SignatureMuiBridge>
    </SignatureContext.Provider>
  );
};

export const useSignature = (): SignatureContextType => {
  const context = useContext(SignatureContext);
  if (!context) {
    throw new Error('useSignature deve ser usado dentro de um SignatureProvider');
  }
  return context;
};
